import React from 'react';

const INITIALS_COLORS = [
  { bg: '#eff6ff', text: '#2563eb' },
  { bg: '#ecfdf5', text: '#16a34a' },
  { bg: '#faf5ff', text: '#9333ea' },
  { bg: '#fffbeb', text: '#d97706' },
  { bg: '#fdf2f8', text: '#db2777' },
  { bg: '#f0fdfa', text: '#0d9488' }
];

function getInitials(name) {
  if (!name) return '??';
  const clean = name.trim();
  if (clean.includes('@')) return clean.split('@')[0].slice(0, 2).toUpperCase();
  const parts = clean.split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : clean.slice(0, 2).toUpperCase();
}

function getInitialsTheme(initials) {
  const code = initials.charCodeAt(0) + (initials.charCodeAt(1) || 0);
  return INITIALS_COLORS[code % INITIALS_COLORS.length];
}

function buildPageNumbers(currentPage, totalPages) {
  // Always 1-indexed for display
  const pg = currentPage + 1;
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (pg > 3) pages.push('...');
  for (let i = Math.max(2, pg - 1); i <= Math.min(totalPages - 1, pg + 1); i++) {
    pages.push(i);
  }
  if (pg < totalPages - 2) pages.push('...');
  pages.push(totalPages);
  return pages;
}

export default function RejectionDetailsTable({
  rejectionDetails = {},
  currentPage = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange
}) {
  const { total = 0, data = [] } = rejectionDetails;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startEntry = total === 0 ? 0 : currentPage * pageSize + 1;
  const endEntry = Math.min((currentPage + 1) * pageSize, total);
  const pageNums = buildPageNumbers(currentPage, totalPages);

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e8ecf0',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Rejection Details</h3>
          <span style={{
            width: '16px', height: '16px', borderRadius: '50%', border: '1px solid #94a3b8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '10px', color: '#94a3b8'
          }}>i</span>
        </div>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            background: 'none', border: 'none', color: '#6366f1',
            fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0
          }}>
            View All Rejections
            <svg style={{ width: 'auto' }} height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div> */}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
        <table style={{ width: '100%', minWidth: '1080px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e8ecf0' }}>
              {['Application ID', 'Applicant Name', 'Applied Date', 'Approved Date',
                'Rejected Stage', 'Rejection Reason', 'Rejected By (Agent)', 'Department', 'Rejected Date']
                .map((col, i) => (
                  <th key={i} style={{
                    padding: '10px 14px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#64748b',
                    textAlign: i === 9 ? 'center' : 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    whiteSpace: 'nowrap'
                  }}>{col}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '36px', color: '#94a3b8', fontSize: '13px' }}>
                  No rejection details found
                </td>
              </tr>
            ) : (
              data.map((row, idx) => {
                const initials = getInitials(row.rejectedBy);
                const theme = getInitialsTheme(initials);
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#4f46e5', fontWeight: '600' }}>{row.applicationId}</td>
                    <td style={{ padding: '11px 14px', fontSize: '13px', color: '#0f172a', fontWeight: '600' }}>{row.applicantName}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#475569' }}>{row.appliedDate}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#475569' }}>{row.approvedDate}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#0d9488', fontWeight: '600' }}>{row.rejectedStage}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#ef4444', fontWeight: '500', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.rejectionReason}>
                      {row.rejectionReason}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          width: '26px', height: '26px', borderRadius: '50%',
                          background: theme.bg, color: theme.text,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '10px', fontWeight: '800', flexShrink: 0
                        }}>{initials}</span>
                        <span style={{ fontSize: '12px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }} title={row.rejectedBy}>
                          {row.rejectedBy}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#475569', textTransform: 'capitalize' }}>{row.department}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: '#475569' }}>{row.rejectedDate}</td>
                    
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
          {total > 0 ? `Showing ${startEntry} to ${endEntry} of ${total.toLocaleString('en-IN')} entries` : 'No entries'}
        </div>

        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Prev */}
            <button
              onClick={() => currentPage > 0 && onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              style={{
                width: '30px', height: '30px', borderRadius: '6px',
                border: '1px solid #e2e8f0', background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                color: currentPage === 0 ? '#cbd5e1' : '#334155',
                padding: 0
              }}
            >
              <svg style={{ width: 'auto' }} height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Page numbers */}
            {pageNums.map((p, i) =>
              p === '...' ? (
                <span key={`dot-${i}`} style={{ fontSize: '13px', color: '#94a3b8', padding: '0 4px' }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p - 1)}
                  style={{
                    width: '30px', height: '30px', borderRadius: '6px',
                    border: '1px solid',
                    borderColor: p === currentPage + 1 ? '#6366f1' : '#e2e8f0',
                    background: p === currentPage + 1 ? '#6366f1' : '#ffffff',
                    color: p === currentPage + 1 ? '#ffffff' : '#334155',
                    fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {p}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => currentPage < totalPages - 1 && onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
              style={{
                width: '30px', height: '30px', borderRadius: '6px',
                border: '1px solid #e2e8f0', background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
                color: currentPage === totalPages - 1 ? '#cbd5e1' : '#334155',
                padding: 0
              }}
            >
              <svg style={{ width: 'auto' }} height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
